import { DateTimeFormatter, Instant, ZoneId } from '@js-joda/core';
import { FieldTypeCategory, isParameterTyped, simplifyType } from '@thinkalpha/language-services';
import { generateExportTimestamp, saveFile } from '@thinkalpha/ui-core/util/csv';
import type { TFunction } from 'i18next';
import { unparse } from 'papaparse';
import type { TableModel } from 'src/models/TableModel';

const excelDateTimeFormatter = DateTimeFormatter.ofPattern('yyyy-MM-dd HH:mm:ss');

export type ExportTableCSVResult =
    | { success: true }
    | { success: false; error: 'no-table' | 'no-columns' | 'no-data' | 'unknown'; message?: string };

/**
 * Exports a table to CSV format and triggers a file download.
 * This is a pure utility function with no React dependencies, suitable for use in model layers.
 *
 * @param table - The table model to export
 * @param columnNames - Map of column IDs to display names (only columns in this map will be exported)
 * @returns Result indicating success or failure with error type
 */
export async function exportTableToCSV(
    table: TableModel | undefined,
    columnNames: Record</* id */ string, /* name */ string>,
    t: TFunction,
): Promise<ExportTableCSVResult> {
    if (!table) {
        return { success: false, error: 'no-table' };
    }

    try {
        const columns = table.columns;
        if (!columns) {
            return { success: false, error: 'no-columns' };
        }

        // Get all rows (max 1 million for practical limits)
        const snapshot = await table.snapshot({ firstRow: 0, windowSize: 1000000 });

        if (!snapshot?.data?.length) {
            return { success: false, error: 'no-data' };
        }

        const rows: Record<string, unknown>[] = [];
        for (const row of snapshot.data) {
            let newRow: Record<string, unknown> = {};
            for (const col of columns) {
                const key = col.id;
                // Only include columns that are in the columnNames map
                if (!columnNames[key]) continue;
                let value = row[key];
                // Format timestamps for Excel compatibility
                if (
                    !isParameterTyped(col.dataType) &&
                    simplifyType(col.dataType) === FieldTypeCategory.Timestamp &&
                    typeof value === 'number'
                ) {
                    const instant = Instant.ofEpochMicro(Math.round(value * 1000));
                    const zoned = instant.atZone(ZoneId.UTC);
                    value = excelDateTimeFormatter.format(zoned);
                }
                newRow = {
                    ...newRow,
                    [columnNames[key]]: value,
                };
            }
            rows.push(newRow);
        }

        const csv = unparse(rows);
        const blob = new Blob([csv], { type: 'text/csv' });
        const filename = t('widgets::export::csv-filename', { timestamp: generateExportTimestamp() });
        saveFile(`${filename}.csv`, blob, 'text/csv');

        return { success: true };
    } catch (e) {
        return {
            success: false,
            error: 'unknown',
            message: e instanceof Error ? e.message : String(e),
        };
    }
}
